import React from "react";
import styled from "styled-components";
import { withFormik, FormikProps } from "formik";
import { Trans, t } from "@lingui/macro";
import { I18n } from "@lingui/react";
import {
  Thirds,
  Center,
  Left,
  Right,
  DisplayMedium,
  DisplaySmall,
  Label,
  Back,
  Button,
  ButtonGroup,
  Select,
  Field,
  FieldText,
  FieldTextarea,
  spacing,
} from "@conserveit/blocks";
import {
  ContentContainer,
  ContentFooter,
  ServerError,
  GoBack,
} from "./Action.styles";

import * as lists from "./utils/lists";
import { valuePair, selectChange, selectBlur } from "./utils/controls";
import {
  formikOptions,
  editActionSchema,
  mapEditActionToProps,
} from "./utils/validation";
import FormikAttachmentField from "./components/FormikAttachmentField";
import { FormFieldGroup } from "./components/FormFieldGroup";
import { ChildField } from "./components/ChildField";
import { ImagePreview } from "./components/ImagePreview";
import { EditFormValues } from "./utils/types";
import { Expandable } from "./components/Expandable";
import EquipListField from "./components/EquipListField";

const StyledLabel = styled(Label)`
  margin-bottom: ${spacing.extraTight};
`;

const StyledThirds = styled(Thirds)`
  margin-bottom: ${spacing.default};
`;

const SubHeading = styled(DisplaySmall)`
  margin-bottom: ${spacing.default};
`;

const Divider = styled.div`
  height: ${spacing.default};
`;

interface OwnProps {
  onSave(values: EditFormValues, equips?: Insite.API.ActionEquip[]): void;
  isPending: boolean;
  equips?: Insite.API.ActionEquip[];
  assignees: Insite.Assignee[];
  submitError?: string;
  action: Insite.API.Action;
  rules: string[];
  urlHistory: Insite.AppState["urlHistory"];
  userGroups: Insite.API.User["groups"];
}

type Props = OwnProps & FormikProps<EditFormValues>;
const EditAction: React.FunctionComponent<Props> = ({
  action: remoteAction,
  assignees,
  isPending,
  submitError,
  urlHistory,
  userGroups,
  equips,

  // FormikBag
  values,
  touched,
  errors,
  handleBlur,
  handleChange,
  handleSubmit,
  setFieldValue,
  setFieldTouched,
}) => {
  const handleRemoveImage = () => {
    setFieldValue("screenshot", null);
    setFieldValue("screenshotLink", "");
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <I18n>
        {({ i18n }) => (
          <React.Fragment>
            <StyledThirds>
              <Left>
                <GoBack to={urlHistory.actionsHistory.path}>
                  <Back
                    text={<Trans id="action_form.back_to_actions">Back</Trans>}
                  />
                </GoBack>
              </Left>
              <Center>
                <DisplayMedium>
                  <Trans id="action_form.edit_action">Edit Action</Trans>{" "}
                  {remoteAction.key}
                </DisplayMedium>
              </Center>
              <Right>
                <ButtonGroup tight>
                  <Button primary thin type="submit" disabled={isPending}>
                    <Trans id="action_form.save">Save</Trans>
                  </Button>
                </ButtonGroup>
              </Right>
            </StyledThirds>
            <ContentContainer>
              <FormFieldGroup
                heading={i18n._(
                  t("action_form.action_details_heading")`Action details`
                )}
              >
                <FieldText
                  label={<Trans id="action_form.summary_label">Summary</Trans>}
                  name="summary"
                  help={
                    <Trans id="action_form.summary_help">
                      Summary of the problem this Action addresses
                    </Trans>
                  }
                  required
                  value={values.summary}
                  disabled={isPending}
                  error={touched.summary && errors.summary}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <FieldTextarea
                  rows={4}
                  label={
                    <Trans id="action_form.description_label">
                      Description
                    </Trans>
                  }
                  name="description"
                  placeholder={i18n._(
                    t(
                      "action_form.description_help"
                    )`More detailed explanation of the action...`
                  )}
                  value={values.description}
                  disabled={isPending}
                  error={touched.summary && errors.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <Field
                  name="priority"
                  label={
                    <Trans id="action_form.priority_label">Priority</Trans>
                  }
                  error={touched.priority && errors.priority}
                  required
                >
                  {({ error, name, id }) => (
                    <Select
                      error={error}
                      name={name}
                      id={id}
                      value={lists
                        .getPriorityOptions(i18n)
                        .find((l) => l.value === values.priority)}
                      disabled={isPending}
                      onChange={selectChange("priority", setFieldValue)}
                      onBlur={selectBlur("priority", setFieldTouched)}
                      options={lists.getPriorityOptions(i18n)}
                    />
                  )}
                </Field>

                <Field
                  name="context"
                  label={<Trans id="action_form.context_label">Context</Trans>}
                  error={touched.context && errors.context}
                  required
                >
                  {({ error, name, id }) => (
                    <Select
                      error={error}
                      name={name}
                      id={id}
                      value={lists
                        .getContextOptions(i18n)
                        .find((l) => l.value === values.context)}
                      disabled={isPending}
                      onChange={selectChange("context", setFieldValue)}
                      onBlur={selectBlur("context", setFieldTouched)}
                      options={lists.getContextOptions(i18n)}
                    />
                  )}
                </Field>
              </FormFieldGroup>

              <FormFieldGroup
                heading={i18n._(t("action_form.status_heading")`Status`)}
              >
                <Field
                  name="status"
                  label={
                    <Trans id="action_form.status_label">Action status</Trans>
                  }
                  error={touched.status && errors.status}
                >
                  {({ error, name, id }) => (
                    <Select
                      error={error}
                      name={name}
                      id={id}
                      value={lists
                        .getStatusOptions(i18n)
                        .find((l) => l.value === values.status)}
                      disabled={isPending}
                      onChange={selectChange("status", setFieldValue)}
                      onBlur={selectBlur("status", setFieldTouched)}
                      options={lists.getStatusOptions(i18n)}
                    />
                  )}
                </Field>

                {values.status !== remoteAction.bonfire_status && (
                  <ChildField>
                    <Field
                      name="transitionReason"
                      label={
                        <Trans id="action_form.transition_label">
                          Reason for change in status
                        </Trans>
                      }
                      help={
                        <Trans id="action_form.transition_help">
                          Adding the correct reason for a change in status helps
                          you track the progress of Actions
                        </Trans>
                      }
                      error={
                        touched.transitionReason && errors.transitionReason
                      }
                    >
                      {({ error, name, id }) => (
                        <Select
                          error={error}
                          name={name}
                          id={id}
                          value={lists
                            .getTransitionReasonOptions(i18n)
                            .find((l) => l.value === values.transitionReason)}
                          disabled={isPending}
                          onChange={selectChange(
                            "transitionReason",
                            setFieldValue
                          )}
                          onBlur={selectBlur(
                            "transitionReason",
                            setFieldTouched
                          )}
                          options={lists.getTransitionReasonOptions(i18n)}
                        />
                      )}
                    </Field>
                  </ChildField>
                )}
              </FormFieldGroup>

              <FormFieldGroup
                heading={i18n._(
                  t("action_form.team_assignment_heading")`Team assignment`
                )}
              >
                <Field
                  name="responsible"
                  label={
                    <Trans id="action_form.responsible_label">
                      Responsible
                    </Trans>
                  }
                  error={touched.responsible && errors.responsible}
                  required
                >
                  {({ error, name, id }) => (
                    <Select
                      error={error}
                      name={name}
                      id={id}
                      value={lists
                        .getResponsibleOptions(
                          userGroups,
                          i18n,
                          remoteAction.project_key
                        )
                        .find((l) => l.value === values.responsible)}
                      disabled={isPending}
                      onChange={selectChange("responsible", setFieldValue)}
                      onBlur={selectBlur("responsible", setFieldTouched)}
                      options={lists.getResponsibleOptions(
                        userGroups,
                        i18n,
                        remoteAction.project_key
                      )}
                    />
                  )}
                </Field>

                <Field
                  name="assignee"
                  label={
                    <Trans id="action_form.assignee_label">Assignee</Trans>
                  }
                  error={touched.assignee && errors.assignee}
                >
                  {({ error, name, id }) => (
                    <Select
                      error={error}
                      name={name}
                      id={id}
                      isClearable
                      value={valuePair(values.assignee)}
                      disabled={isPending}
                      onChange={selectChange("assignee", setFieldValue)}
                      onBlur={selectBlur("assignee", setFieldTouched)}
                      // TODO: NEW ASSIGNEE OPTIONS
                      options={
                        assignees
                          ? assignees.map((a) => a.display).map(valuePair)
                          : []
                      }
                    />
                  )}
                </Field>
              </FormFieldGroup>

              {equips && equips.length > 0 && (
                <FormFieldGroup
                  heading={i18n._(
                    t("action_form.rule_heading")`Rules and equips`
                  )}
                >
                  <EquipListField equips={equips} />
                </FormFieldGroup>
              )}

              <FormFieldGroup
                heading={i18n._(
                  t("action_form.resources_heading")`Other resources`
                )}
              >
                <FieldText
                  label={<Trans id="action_form.report_url">Report URL</Trans>}
                  name="skysparkLink"
                  value={values.skysparkLink}
                  disabled={isPending}
                  error={touched.skysparkLink && errors.skysparkLink}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <StyledLabel as="div">
                  <Trans render="label" id="action_form.attach_screenshot">
                    Attach screenshot
                  </Trans>
                </StyledLabel>
                {values.screenshotLink && (
                  <ImagePreview
                    src={values.screenshotLink}
                    onRemove={handleRemoveImage}
                  />
                )}
                <FormikAttachmentField
                  name="screenshot"
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                />
              </FormFieldGroup>

              <FormFieldGroup
                heading={i18n._(
                  t("action_form.estimated_savings_heading")`Estimated savings`
                )}
              >
                <Expandable visible={false}>
                  <SubHeading>
                    {i18n._(
                      t(
                        "action_form.estimated_savings_utility"
                      )`Utility savings`
                    )}
                  </SubHeading>

                  <FieldText
                    label={
                      <Trans id="action_form.electricity_label">
                        Electricity (kWh)
                      </Trans>
                    }
                    type="number"
                    name="elec"
                    help={
                      <Trans id="action_form.electricity_help">
                        Estimated annual loss
                      </Trans>
                    }
                    value={values.elec}
                    disabled={isPending}
                    error={touched.elec && errors.elec}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <FieldText
                    label={<Trans id="action_form.gas_label">Gas (GJ)</Trans>}
                    type="number"
                    name="gas"
                    help={
                      <Trans id="action_form.gas_help">
                        Estimated annual loss
                      </Trans>
                    }
                    value={values.gas}
                    disabled={isPending}
                    error={touched.gas && errors.gas}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <FieldText
                    label={
                      <Trans id="action_form.water_label">Water (kL)</Trans>
                    }
                    type="number"
                    name="water"
                    help={
                      <Trans id="action_form.water_help">
                        Estimated annual loss
                      </Trans>
                    }
                    value={values.water}
                    disabled={isPending}
                    error={touched.water && errors.water}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <Divider />
                  <SubHeading>
                    {i18n._(
                      t("action_form.estimated_savings_cost")`Cost savings`
                    )}
                  </SubHeading>
                  <FieldText
                    label={
                      <Trans id="action_form.savings_label">
                        Estimated energy savings ($)
                      </Trans>
                    }
                    type="number"
                    name="totalFinancialImpact"
                    help={
                      <Trans id="action_form.savings_help_energy">
                        Expected annual performance impact if the issue is not
                        resolved
                      </Trans>
                    }
                    value={values.totalFinancialImpact}
                    disabled={isPending}
                    error={
                      touched.totalFinancialImpact &&
                      errors.totalFinancialImpact
                    }
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <FieldText
                    label={
                      <Trans id="action_form.operational_savings_label">
                        Operational savings ($)
                      </Trans>
                    }
                    type="number"
                    name="operationalSavings"
                    help={
                      <Trans id="action_form.savings_help_operational">
                        Expected annual non-utility impact if the issue is not
                        resolved
                      </Trans>
                    }
                    value={values.operationalSavings}
                    disabled={isPending}
                    error={
                      touched.operationalSavings && errors.operationalSavings
                    }
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Expandable>
              </FormFieldGroup>

              {submitError !== "" && <ServerError>{submitError}</ServerError>}

              <ContentFooter>
                <ButtonGroup>
                  <Button primary type="submit" disabled={isPending}>
                    <Trans id="action_form.save_changes">Save Changes</Trans>
                  </Button>
                </ButtonGroup>
              </ContentFooter>
            </ContentContainer>
          </React.Fragment>
        )}
      </I18n>
    </form>
  );
};

EditAction.defaultProps = {
  submitError: "",
  equips: [],
};

const withForm = withFormik<OwnProps, EditFormValues>({
  ...formikOptions,
  // TODO: Should be ReturnType<typeof serialiseEditAction> probably
  mapPropsToValues: mapEditActionToProps as any,
  validationSchema: editActionSchema,
  handleSubmit: (values, { props }) => {
    props.onSave(values, props.equips);
  },
})(EditAction);

export { withForm as EditAction };
